import React from 'react'
import { useI18n } from '../../../../i18n/I18nContext'

/**
 * Android-parity presentation primitives for the phone worker shell.
 *
 * These mirror the Compose prototype's cockpit language — white cards on a
 * light slate background, status chips, 48px touch targets, a bottom sheet —
 * using the project's own design tokens so runtime tenant branding still
 * applies. No second brand system is introduced here.
 */

export type MobileTone = 'neutral' | 'positive' | 'warning' | 'critical' | 'info'

export const MobileCard: React.FC<{
  children: React.ReactNode
  tone?: MobileTone
  as?: 'article' | 'section' | 'div'
  testId?: string
  label?: string
  id?: string
}> = ({ children, tone = 'neutral', as = 'article', testId, label, id }) => {
  const Tag = as
  return (
    <Tag id={id} className="wm-card" data-tone={tone} data-testid={testId} aria-label={label}>
      {children}
    </Tag>
  )
}

export const MobileChip: React.FC<{ tone?: MobileTone; children: React.ReactNode; testId?: string }> = ({ tone = 'neutral', children, testId }) => (
  <span className="wm-chip" data-tone={tone} data-testid={testId}>{children}</span>
)

/** Backend status strings drive the tone; the client never reinterprets them. */
export const statusTone = (status?: string | null): MobileTone => {
  const normalized = String(status || '').toLowerCase()
  if (['active', 'approved', 'confirmed', 'punched_in', 'in', 'synced', 'complete', 'completed'].includes(normalized)) return 'positive'
  if (['break', 'pending', 'pending_review', 'submitted', 'awaiting', 'shortlisted', 'requires_review'].includes(normalized)) return 'warning'
  if (['cancelled', 'declined', 'rejected', 'failed', 'expired'].includes(normalized)) return 'critical'
  return 'neutral'
}

export const MobileSectionTitle: React.FC<{ title: string; detail?: string; action?: React.ReactNode }> = ({ title, detail, action }) => (
  <header className="wm-section-title">
    <div>
      <h2>{title}</h2>
      {detail ? <p>{detail}</p> : null}
    </div>
    {action}
  </header>
)

export const MobilePrimaryButton: React.FC<{
  label: string
  onClick: () => void
  disabled?: boolean
  tone?: MobileTone
  busy?: boolean
  testId?: string
}> = ({ label, onClick, disabled, tone = 'info', busy, testId }) => (
  <button
    type="button"
    className="wm-button wm-button--primary"
    data-tone={tone}
    data-testid={testId}
    onClick={onClick}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
  >
    {label}
  </button>
)

export const MobileSecondaryButton: React.FC<{
  label: string
  onClick: () => void
  disabled?: boolean
  testId?: string
}> = ({ label, onClick, disabled, testId }) => (
  <button type="button" className="wm-button wm-button--secondary" data-testid={testId} onClick={onClick} disabled={disabled}>
    {label}
  </button>
)

export const MobileListRow: React.FC<{
  title: string
  detail?: string
  trailing?: React.ReactNode
  onClick?: () => void
  testId?: string
}> = ({ title, detail, trailing, onClick, testId }) => {
  const content = (
    <>
      <span className="wm-row__text">
        <strong>{title}</strong>
        {detail ? <small>{detail}</small> : null}
      </span>
      {trailing ? <span className="wm-row__trailing">{trailing}</span> : null}
    </>
  )
  if (!onClick) return <div className="wm-row" data-testid={testId}>{content}</div>
  return (
    <button type="button" className="wm-row wm-row--action" data-testid={testId} onClick={onClick}>
      {content}
    </button>
  )
}

export const MobileEmptyState: React.FC<{ title: string; detail: string; testId?: string }> = ({ title, detail, testId }) => (
  <div className="wm-state" data-testid={testId}>
    <strong>{title}</strong>
    <p>{detail}</p>
  </div>
)

export const MobileLoadingState: React.FC<{ label?: string }> = ({ label }) => {
  const { t } = useI18n()
  return (
    <div className="wm-state wm-state--loading" role="status" aria-live="polite">
      <span className="wm-spinner" aria-hidden="true" />
      <p>{label || t('Loading your shifts…')}</p>
    </div>
  )
}

export const MobileErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => {
  const { t } = useI18n()
  return (
    <div className="wm-state wm-state--error" role="alert">
      <strong>{t('We couldn’t load this section.')}</strong>
      <p>{message}</p>
      <MobileSecondaryButton label={t('Try again')} onClick={onRetry} />
    </div>
  )
}

/**
 * Bottom sheet. Modal, focus-trapped at the edges, and dismissible by Escape or
 * the scrim — matching the Compose ModalBottomSheet behavior workers already
 * know from Android.
 */
export const MobileSheet: React.FC<{
  title: string
  open: boolean
  onClose: () => void
  children: React.ReactNode
  testId?: string
}> = ({ title, open, onClose, children, testId }) => {
  const { t } = useI18n()
  const headingId = React.useId()
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const restoreFocusTo = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!open) return undefined
    // Remember what opened the sheet so focus can be returned there on close.
    restoreFocusTo.current = document.activeElement as HTMLElement | null

    const focusable = () => Array.from(
      sheetRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []
    ).filter((element) => element.offsetParent !== null || element === document.activeElement)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      // Trap focus inside the dialog: a modal must not let Tab reach the page
      // behind it.
      const elements = focusable()
      if (!elements.length) {
        event.preventDefault()
        sheetRef.current?.focus()
        return
      }
      const first = elements[0]
      const last = elements[elements.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === sheetRef.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    const initial = focusable()[0]
    if (initial) initial.focus()
    else sheetRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      restoreFocusTo.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="wm-sheet-scrim" onClick={onClose} data-testid={testId ? `${testId}-scrim` : undefined}>
      <div
        className="wm-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        ref={sheetRef}
        onClick={(event) => event.stopPropagation()}
        data-testid={testId}
      >
        <div className="wm-sheet__grabber" aria-hidden="true" />
        <header className="wm-sheet__header">
          <h2 id={headingId}>{title}</h2>
          <button type="button" className="wm-icon-button" onClick={onClose} aria-label={t('Close')}>×</button>
        </header>
        <div className="wm-sheet__body">{children}</div>
      </div>
    </div>
  )
}

/**
 * Honest capability explanation. Used everywhere a browser cannot do something
 * the Android app can, so the worker sees a reason instead of a broken control.
 */
export const MobileCapabilityNotice: React.FC<{
  title: string
  detail: string
  action?: { label: string; onClick: () => void }
  tone?: MobileTone
  testId?: string
}> = ({ title, detail, action, tone = 'info', testId }) => (
  <div className="wm-notice" data-tone={tone} role="note" data-testid={testId}>
    <strong>{title}</strong>
    <p>{detail}</p>
    {action ? <MobileSecondaryButton label={action.label} onClick={action.onClick} /> : null}
  </div>
)
